;(() => {
  const namespace = (globalThis.StylistLocator = globalThis.StylistLocator || {})

  // ============================================================================
  // Dependencies
  // ============================================================================

  const { templates = {}, utils = {} } = namespace
  const { buildGalleryMarkup = () => '' } = templates
  const { escapeHtml = (v) => (v == null ? '' : String(v)), encodeKey = (v) => v } = utils

  const DEFAULT_ROLE_LABEL = 'IBE® Stylist'
  const DEFAULT_STYLIST_NAME = 'Unnamed Stylist'

  // ============================================================================
  // Read More Functionality
  // ============================================================================

  /**
   * Initialize "read more" buttons for description text.
   * @param {HTMLElement} scope - Container to search for buttons
   */
  function initializeReadMore(scope) {
    if (!scope) return

    scope.querySelectorAll('[data-read-more]').forEach((button) => {
      const wrapper = button.closest('.stylist-locator__info-description-wrapper') || scope
      const description = wrapper.querySelector('[data-description-text]')

      if (!description) {
        button.remove()
        return
      }

      const isOverflowing = description.scrollHeight - description.clientHeight > 1
      if (!isOverflowing) {
        button.remove()
        description.classList.remove('stylist-locator__info-description--expanded')
        return
      }

      button.addEventListener('click', () => {
        const isExpanded = description.classList.toggle('stylist-locator__info-description--expanded')
        button.textContent = isExpanded ? 'read less' : 'read more'
      })
    })
  }

  // ============================================================================
  // Profile Sections
  // ============================================================================

  /**
   * Generate profile section data for a stylist.
   * @param {Object} stylist - Stylist object
   * @returns {Object} Profile sections with escaped HTML
   */
  function getProfileSections(stylist) {
    const safeName = escapeHtml(stylist.name || DEFAULT_STYLIST_NAME)
    // If stylist is a certified salon without a salon name, display "Certified Salon" as role
    const isCertifiedSalonWithoutName = (stylist.isCertifiedSalon === true || stylist.isSalonOwner === true) && !stylist.salonName
    const roleText = isCertifiedSalonWithoutName ? 'Certified Salon' : (stylist.role || DEFAULT_ROLE_LABEL)
    const safeRole = escapeHtml(roleText)
    const safeSalonName = stylist.salonName ? escapeHtml(stylist.salonName) : ''
    const safeAddress = stylist.address ? escapeHtml(stylist.address) : ''

    const avatarMarkup = buildAvatarMarkup(stylist, safeName)
    const descriptionMarkup = buildDescriptionMarkup(stylist.description)
    const tagsMarkup = buildTagsMarkup(stylist)

    return {
      name: safeName,
      role: safeRole,
      salonName: safeSalonName,
      address: safeAddress,
      avatar: avatarMarkup,
      description: descriptionMarkup,
      tags: tagsMarkup,
      gallery: buildGalleryMarkup(stylist),
    }
  }

  /**
   * Render a single tag badge.
   */
  function renderTagMarkup(label, value) {
    if (!value) return ''
    return `<span class="stylist-locator__tag stylist-locator__tag--primary">${escapeHtml(label)}</span>`
  }

  function getInitials(name) {
    return (name || '')
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
  }

  function buildAvatarMarkup(stylist, safeName) {
    if (stylist.profile_photo) {
      const avatarSrc = escapeHtml(stylist.profile_photo)
      return `<img src="${avatarSrc}" alt="${safeName}">`
    }

    const initials = getInitials(stylist.name)
    return `<span>${initials || 'IB'}</span>`
  }

  function buildDescriptionMarkup(description) {
    if (!description) return ''
    const descriptionText = escapeHtml(description)
    return `
      <div class="stylist-locator__info-description-wrapper">
        <p class="stylist-locator__info-description" data-description-text>${descriptionText}</p>
        <button type="button" class="stylist-locator__info-read-more" data-read-more>read more</button>
      </div>
    `
  }

  function buildTagsMarkup(stylist) {
    const tagBadges = [
      renderTagMarkup('Business', stylist.business),
      renderTagMarkup('Cutting', stylist.cutting),
      renderTagMarkup('Color', stylist.color),
      renderTagMarkup('Technique', stylist.technique),
    ].filter(Boolean)

    if (!tagBadges.length) {
      return ''
    }

    return `
      <div class="stylist-locator__info-tags-section">
        <p class="stylist-locator__info-tags-title">Graduate of Masters</p>
        <div class="stylist-locator__info-tags">${tagBadges.join('')}</div>
      </div>
    `
  }

  // ============================================================================
  // Consult Modal Content
  // ============================================================================

  const LOCATION_PIN_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none"><path d="M5 0C3.67484 0 2.40443 0.526784 1.46717 1.46447C0.529913 2.40215 0.00329 3.67392 0 5V5.008C0.00329 7.64 2.808 10.76 4.26 12.288C4.456 12.496 4.723 12.612 5 12.612C5.277 12.612 5.544 12.496 5.74 12.288C7.2 10.752 10 7.632 10 5.008V5C9.99671 3.67392 9.47009 2.40215 8.53283 1.46447C7.59557 0.526784 6.32516 0 5 0ZM5 7C4.70333 7 4.41332 6.91203 4.16665 6.7472C3.91997 6.58238 3.72771 6.34811 3.61418 6.07403C3.50065 5.79994 3.47094 5.49834 3.52882 5.20737C3.5867 4.91639 3.72956 4.64912 3.93934 4.43934C4.14912 4.22956 4.41639 4.0867 4.70737 4.02882C4.99834 3.97094 5.29994 4.00065 5.57403 4.11418C5.84812 4.22771 6.08238 4.41997 6.2472 4.66665C6.41203 4.91332 6.5 5.20333 6.5 5.5C6.5 5.89783 6.34196 6.27936 6.06066 6.56066C5.77936 6.84196 5.39783 7 5 7Z" fill="#060606"/></svg>'

  const CLOSE_X_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M11 1L1 11M1 1L11 11" stroke="#060606" stroke-width="1.5" stroke-linecap="round"/></svg>'

  /**
   * Render consult modal content for a stylist.
   * @param {Object} stylist - Stylist object
   * @returns {string} HTML content for the modal
   */
  function renderConsultModal(stylist) {
    if (!stylist) return ''

    const sections = getProfileSections(stylist)

    const { geometry = {} } = namespace
    const { determineRoleCategory = () => 'default' } = geometry
    const roleCategory = stylist.roleCategory || determineRoleCategory(stylist)

    const isSalonOnly = stylist.isSalonOwner === true && !(stylist.role && stylist.role !== 'Certified Salon')
    const roleLabel = isSalonOnly ? 'Certified Salon' : (stylist.role || '')

    const badgeMarkup = roleLabel && roleCategory !== 'default'
      ? `<span class="stylist-locator__modal-badge">${escapeHtml(roleLabel)}</span>`
      : ''

    const addressMarkup = sections.address
      ? `<p class="stylist-locator__modal-address">${LOCATION_PIN_SVG}<span>${sections.address}</span></p>`
      : ''

    return `
      <div class="stylist-locator__modal-header">
        <div class="stylist-locator__modal-avatar">${sections.avatar}</div>
        <div class="stylist-locator__modal-meta">
          <div class="stylist-locator__modal-name-row">
            <div class="stylist-locator__modal-names">
              <p class="stylist-locator__modal-name">${sections.name}</p>
              ${sections.salonName ? `<p class="stylist-locator__modal-salon">${sections.salonName}</p>` : ''}
            </div>
            ${badgeMarkup ? `<span class="stylist-locator__modal-badge stylist-locator__modal-badge--mobile">${escapeHtml(roleLabel)}</span>` : ''}
            <button type="button" class="stylist-locator__modal-close" data-modal-close>
              <span>CLOSE</span>${CLOSE_X_SVG}
            </button>
          </div>
          <div class="stylist-locator__modal-badge-row">
            ${badgeMarkup}
            ${addressMarkup}
          </div>
          ${addressMarkup ? `<div class="stylist-locator__modal-address-row--mobile">${addressMarkup}</div>` : ''}
        </div>
      </div>
      <div class="stylist-locator__modal-form-section">
        <div class="stylist-locator__modal-form-header">
          <h3 class="stylist-locator__modal-form-title">Request a Consultation</h3>
          <p class="stylist-locator__modal-form-subtitle">Share a few details and your stylist will reach out to schedule your consultation.</p>
        </div>
        <form class="stylist-locator__modal-form" data-modal-form>
          <div class="stylist-locator__modal-field">
            <div class="stylist-locator__modal-field-label">
              <span class="stylist-locator__modal-field-label-text">Name</span>
              <span class="stylist-locator__modal-field-label-hint">Required</span>
            </div>
            <input type="text" placeholder="Name" data-modal-name required>
          </div>
          <div class="stylist-locator__modal-field">
            <div class="stylist-locator__modal-field-label">
              <span class="stylist-locator__modal-field-label-text">Email</span>
              <span class="stylist-locator__modal-field-label-hint">Required</span>
            </div>
            <input type="email" placeholder="Email Address" data-modal-email required>
          </div>
          <div class="stylist-locator__modal-field">
            <div class="stylist-locator__modal-field-label">
              <span class="stylist-locator__modal-field-label-text">Phone Number</span>
              <span class="stylist-locator__modal-field-label-hint">Required</span>
            </div>
            <input type="tel" placeholder="Phone Number" data-modal-phone required>
          </div>
          <div class="stylist-locator__modal-field">
            <div class="stylist-locator__modal-field-label">
              <span class="stylist-locator__modal-field-label-text">Tell us a bit about your hair and what you’re hoping to achieve.</span>
              <span class="stylist-locator__modal-field-label-hint">Required</span>
            </div>
            <textarea placeholder="Share length, texture, current color, prior extensions, condition, and goals." data-modal-how-did-you-hear rows="3" required></textarea>
          </div>
          <button type="submit" class="stylist-locator__modal-submit" data-modal-submit>
            <span class="stylist-locator__modal-submit-text--desktop">SUBMIT</span>
          </button>
        </form>
      </div>
      <div class="stylist-locator__modal-footer">
        <button type="button" class="stylist-locator__modal-submit" data-modal-submit-mobile>
          <span>SUBMIT CONSULT REQUEST</span>
        </button>
        <button type="button" class="stylist-locator__modal-back" data-modal-back>
          <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M5 9L1 5L5 1" stroke="#060606" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M1 5H9" stroke="#060606" stroke-width="1.5" stroke-linecap="round"/></svg>
          <span>BACK TO STYLIST DETAILS</span>
        </button>
      </div>
    `
  }


  // ============================================================================
  // Exports
  // ============================================================================

  namespace.info = Object.assign({}, namespace.info, {
    initializeReadMore,
    getProfileSections,
    renderTagMarkup,
    renderConsultModal,
  })
})()
